import { useTranslator } from '@bluebeam/react-i18n';
import Gear from 'components/Icons/Gear';
import BasicBlueprint from 'components/Icons/BasicBlueprint';
import Dashboard from 'components/Icons/Dashboard';
import FileIcon from 'components/Icons/File';
import QuestionSpeechBubble from 'components/Icons/QuestionSpeechBubble';
import Submittal from 'components/Icons/Submittal';
import TapeRoll from 'components/Icons/TapeRoll';
import FileManagerModal from 'components/modals/FileManagerModal';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { OrderedMap } from 'immutable';
import React, { useEffect } from 'react';
import { ModalState } from 'records/ModalState';
import { ProcessingStatus } from 'records/ProcessingStatus';
import { Project } from 'records/Project';
import { BatchUploadProgress } from 'records/UploadProgress';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { AECIcons } from '@bluebeam/react-skyline';
import { useLocation } from 'react-router-dom';
import TwoLineSideNavItem from './TwoLineNavItem';
import useAdornerState from '../../../hooks/useAdornerState';
import FilesNav, { AdornerStateType } from './FilesNav';
import SideNavItem from './SideNavItem';

const mergeAdornerStates = (states: AdornerStateType[]): AdornerStateType => {
  if (states.includes('ERROR')) {
    return 'ERROR';
  }
  if (states.includes('INPROGRESS')) {
    return 'INPROGRESS';
  }
  if (states.includes('COMPLETED')) {
    return 'COMPLETED';
  }
  return 'NONE';
};

const getUploadState = (
  activeUploads: OrderedMap<string, BatchUploadProgress>,
  recentUploads: OrderedMap<string, BatchUploadProgress>,
  processingStatuses: OrderedMap<string, ProcessingStatus>
): AdornerStateType => {
  const hasUploadErrors = recentUploads.some((u) =>
    u.documentsProgress.some((d) => {
      return d.error && d.error.length > 0;
    })
  );

  if (hasUploadErrors) {
    return 'ERROR';
  }

  const someIncompleteUploads = Array.from(activeUploads.values()).some(
    (re) => re.totalUploaded !== re.totalToUpload
  );

  if (someIncompleteUploads) {
    return 'INPROGRESS';
  }

  const hasUploadProcessing = processingStatuses.some(
    (ps) => ps.status === 'Processing'
  );

  if (hasUploadProcessing) {
    return 'COMPLETED';
  }

  return 'NONE';
};

const DefaultSideNavContent: React.FC<WithAppStoreProps> = ({
  actions,
  appStore,
}) => {
  const { state } = appStore;
  const {
    selectedProject,
    projects,
    activeUploads,
    processingStatuses,
    recentUploads,
  } = state;

  const { translate } = useTranslator(['defaultSideNavContent']);
  const {
    [FeatureFlag.ProjectStorage]: isProjectStorageEnabled,
    [FeatureFlag.Submittals]: isSubmittalsEnabled,
    [FeatureFlag.GumballGuardiansPrototype]: isGumballGuardiansPrototypeEnabled,
  } = useFeatureFlags();
  const location = useLocation();

  const { permissions } = projects.get(selectedProject, new Project());

  const uploadState = getUploadState(
    activeUploads,
    recentUploads,
    processingStatuses
  );
  const { syncAdornerState, clearAdornerState } = useAdornerState();
  const adornerState = mergeAdornerStates([uploadState, syncAdornerState]);

  useEffect(() => {
    if (!selectedProject) {
      return;
    }

    actions.getRecentUploads();
  }, [selectedProject]);

  const openFileManagerModal = async (): Promise<void> => {
    clearAdornerState();

    const modalState = new ModalState({
      id: 'fileManagerModal',
      portal: 'main',
      open: true,
      component: FileManagerModal,
    });
    actions.createModal(modalState);
  };

  return (
    <div id="default-side-nav">
      <style>
        {`
          .sidenav :not(a.active) svg {
            fill: var(--color-white);
          }

          .sidenav a.active svg {
            fill: var(--color-primary);
            fill: url(#awesome-gradient);
          }
          `}
      </style>
      <svg height="0" width="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient
            id="awesome-gradient"
            x1="0"
            y1="0"
            x2="1"
            y2="0"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(1.46958e-15,24,-24,1.46958e-15,10,0)"
          >
            <stop offset="0" style={{ stopColor: '#00ace8', stopOpacity: 1 }} />
            <stop offset="1" style={{ stopColor: '#0074cc', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>
      <div className="sidenav">
        <>
          <SideNavItem
            id="default-side-nav-dashboard"
            text={translate('defaultSideNavContent:dashboard')}
            icon={<Dashboard width="20px" height="20px" />}
            to={`/project/${selectedProject}/dashboard`}
            testId="side-nav-dashboard"
          />
          <SideNavItem
            id="default-side-nav-drawings"
            text={translate('defaultSideNavContent:drawings')}
            icon={<BasicBlueprint width="20px" height="20px" />}
            to={`/project/${selectedProject}/drawings`}
          />
        </>
        {isProjectStorageEnabled && (
          <SideNavItem
            id="default-side-nav-documents"
            text={translate('defaultSideNavContent:documents')}
            icon={<FileIcon width="20px" height="24px" viewBox="3 2 18 20" />}
            to={`/project/${selectedProject}/documents`}
            testId="side-nav-documents"
          />
        )}
        <>
          <SideNavItem
            id="default-side-nav-punch"
            text={translate('defaultSideNavContent:punch')}
            icon={<TapeRoll width="18px" height="20px" />}
            to={`/project/${selectedProject}/punches`}
          />
          <SideNavItem
            id="default-side-nav-rfis"
            text={translate('defaultSideNavContent:rfis')}
            icon={<QuestionSpeechBubble width="20px" height="20px" />}
            to={`/project/${selectedProject}/rfis`}
          />
          {isSubmittalsEnabled ? (
            <SideNavItem
              id="default-side-nav-submittals"
              text={translate('defaultSideNavContent:submittals')}
              icon={<Submittal width="20px" height="24px" />}
              to={`/project/${selectedProject}/submittals`}
            />
          ) : null}
          {isGumballGuardiansPrototypeEnabled && (
            <SideNavItem
              id="default-side-nav-open-pdf-base"
              text={translate('defaultSideNavContent:psdfKit')}
              icon={<QuestionSpeechBubble width="20px" height="20px" />}
              to={`/project/${selectedProject}/pspdfBase`}
            />
          )}
          {isGumballGuardiansPrototypeEnabled && (
            <SideNavItem
              id="default-side-nav-open-pdf-proto"
              text={translate('defaultSideNavContent:psdfKitPlus')}
              icon={<QuestionSpeechBubble width="20px" height="20px" />}
              to={`/project/${selectedProject}/pspdfProto`}
            />
          )}
          {isGumballGuardiansPrototypeEnabled ? (
            <SideNavItem
              id="default-side-nav-custom-editor"
              text={translate('defaultSideNavContent:custom')}
              icon={<Submittal width="20px" height="24px" />}
              to={`/project/${selectedProject}/custom`}
            />
          ) : null}
          {isGumballGuardiansPrototypeEnabled && (
            <SideNavItem
              id="default-side-nav-open-pdf-tron"
              text={translate('defaultSideNavContent:pdfTron')}
              icon={<QuestionSpeechBubble width="20px" height="20px" />}
              to={`/project/${selectedProject}/pdftron`}
            />
          )}
          {isGumballGuardiansPrototypeEnabled && (
            <SideNavItem
              id="default-side-nav-open-pdf-tron"
              text={translate('defaultSideNavContent:tronAnnot')}
              icon={<BasicBlueprint width="20px" height="20px" />}
              to={`/project/${selectedProject}/pdftron-annot`}
            />
          )}
          {isGumballGuardiansPrototypeEnabled && (
            <SideNavItem
              id="default-side-nav-open-pdf-tron"
              text={translate('defaultSideNavContent:tronOverlay')}
              icon={<QuestionSpeechBubble width="20px" height="20px" />}
              to={`/project/${selectedProject}/pdftron-overlay`}
            />
          )}
          {isGumballGuardiansPrototypeEnabled && (
            <SideNavItem
              id="default-side-nav-open-pdf-js"
              text={translate('defaultSideNavContent:pdfJs')}
              icon={<QuestionSpeechBubble width="20px" height="20px" />}
              to={`/project/${selectedProject}/pdf-js`}
            />
          )}
          <div className="sideNavFooter absolute bottom-0 w100">
            {permissions.viewUsers ? (
              <SideNavItem
                id="default-side-nav-settings"
                text={translate('defaultSideNavContent:settings')}
                icon={<Gear width="23px" height="23px" />}
                to={`/project/${selectedProject}/settings`}
                dataAnalytics="project-nav-settings"
              />
            ) : null}
          </div>
        </>
      </div>
    </div>
  );
};

export default withAppStore(DefaultSideNavContent);
